"use client";

/**
 * Lightweight first-party pageview + engagement tracker.
 *
 * Contract:
 *   - `trackPageview(path, locale, isSpaNav)` fires a fresh POST
 *     and starts a new engagement session for that path.
 *   - `trackHeartbeat()` flushes the accumulated engaged time to
 *     the current path's page_views row.
 *   - `trackPageEnd()` is the pagehide / unload hook - issues a
 *     final sendBeacon so the last partial 15 s doesn't get lost.
 *
 * Engagement time is measured as "visible + focused" wall-clock,
 * not raw wall clock. Tabs backgrounded for an hour don't inflate
 * the number. The client accumulates ms in-memory since the last
 * heartbeat and the server writes the running total onto the row
 * via UPSERT - no per-ping rows in the table.
 *
 * Session continuity:
 *   - session_id lives in localStorage keyed by ``menzil-track-session``.
 *   - Idle rollover at 30 min: if the previous heartbeat was more than
 *     30 min ago we mint a new UUID and flag is_first_in_session=true.
 *     Matches the industry-standard definition used by most analytics
 *     vendors so "sessions" read the same here as they would in GA.
 */

import { API_BASE } from "@/lib/api";
import { peekFingerprint, warmFingerprintLater } from "@/lib/fingerprint";
import { getClientId } from "@/lib/client-id";
import { captureEntry, noteVia } from "@/lib/entry-context";
import { IS_CRAWLER } from "@/lib/crawler";

// ── Constants ─────────────────────────────────────────────────────

const SESSION_KEY = "menzil-track-session";
const SESSION_IDLE_MS = 30 * 60 * 1000;
const MAX_HEARTBEAT_MS = 60 * 60 * 1000; // server-side cap mirror

interface StoredSession {
  id: string;
  last_activity: number;
}

interface ActiveVisit {
  session_id: string;
  path: string;
  // Locale at pageview time. Picked up by trackEvent so event rows
  // carry the same locale axis as their matching page_views row.
  locale: string | null;
  // Total engaged ms accumulated since page mount. Updated by the
  // heartbeat loop; flushed to the server on each heartbeat.
  duration_ms: number;
  // Timestamp of the last transition into the "visible+focused" state.
  // Adding (now - this) whenever we flush gives the fresh engaged delta.
  visible_since: number;
  // True when currently visible + focused. Governs whether the
  // heartbeat tick adds time or skips.
  is_active: boolean;
}

let active: ActiveVisit | null = null;
const pendingEvents: Array<[string, Record<string, unknown> | undefined]> = [];

// ── Helpers ──────────────────────────────────────────────────────

function uuidv4(): string {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // Cheap fallback for old browsers - not RFC-grade, but unique
  // enough for a session key.
  return (
    Math.random().toString(36).slice(2) +
    Date.now().toString(36) +
    Math.random().toString(36).slice(2)
  );
}

function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.id === "string" && typeof parsed.last_activity === "number") {
      return parsed as StoredSession;
    }
  } catch {
    /* ignore */
  }
  return null;
}

/** The id of the browsing session the page views carry right now, or
 *  null once the idle window has passed (the next page view starts a new
 *  one). The replay recorder stamps it on a recording so the admin user
 *  page can open the recording from the session row. */
export function currentTrackSessionId(): string | null {
  const stored = readSession();
  return stored && Date.now() - stored.last_activity < SESSION_IDLE_MS ? stored.id : null;
}

function writeSession(session: StoredSession): void {
  try {
    localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  } catch {
    /* quota / private mode - pageview still fires, just no persistence */
  }
}

function detectClientSource(): string {
  if (typeof window !== "undefined") {
    try {
      // Native mobile shell (mobile/, Capacitor) appends this token via
      // appendUserAgent. Checked before the PWA heuristics because the
      // shell's WebView can also report standalone display-mode.
      if (navigator.userAgent.includes("MenzilApp/")) return "app";
      if (window.matchMedia?.("(display-mode: standalone)").matches) return "pwa";
      // iOS Safari home-screen apps don't surface display-mode reliably
      // across all OS versions; fall back to the legacy `navigator.standalone`
      // flag so iPhone PWAs still tag as PWA on the page-views table.
      if ((window.navigator as Navigator & { standalone?: boolean }).standalone) {
        return "pwa";
      }
    } catch {
      /* ignore */
    }
  }
  return "web";
}

/**
 * POST a tracking payload. Uses `fetch` with `keepalive` by default so
 * the request survives fast navigations. For the pagehide path we fall
 * back to sendBeacon because some browsers discard keepalive-fetch in
 * that exact moment. Fire-and-forget: we never block on the response.
 */
function post(path: string, body: Record<string, unknown>, useBeacon = false): void {
  // Crawlers rendering the page: every beacon cost a crawl request
  // (124 k on api.menzil.ai in three months) and the server drops bot
  // rows anyway (lib/crawler).
  if (IS_CRAWLER) return;
  const url = `${API_BASE}${path}`;
  const json = JSON.stringify(body);
  if (useBeacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
    try {
      const blob = new Blob([json], { type: "application/json" });
      if (navigator.sendBeacon(url, blob)) return;
    } catch {
      /* fall through to fetch */
    }
  }
  // Identity: the client ID is synchronous (localStorage) and is the
  // primary identity axis; the fingerprint is an async engine run and
  // only an anti-abuse/legacy-grouping signal. Tracking never waits for
  // it and never starts it during page load: it rides along once some
  // other call (or warmFingerprintLater, after the first interaction)
  // has computed it. The first pageview of a session carries the
  // UTM/click-id attribution, and awaiting the engine there meant
  // bounce-before-send lost exactly the paid-traffic rows we care most
  // about; it used to get a 250 ms window, but the engine kept running
  // past it and cost a phone 250-700 ms of main thread on every load.
  void (async () => {
    const fp = peekFingerprint();
    if (!fp && !useBeacon) warmFingerprintLater();
    // On the pagehide fallback (sendBeacon unavailable/refused) the
    // document is tearing down - any timer-based wait means the fetch
    // never fires. Send immediately; identity rides on X-Client-Id.
    try {
      await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Client-Source": detectClientSource(),
          "X-Client-Id": getClientId(),
          ...(fp ? { "X-Fingerprint": fp } : {}),
        },
        body: json,
        credentials: "include",
        keepalive: true,
      });
    } catch {
      /* silent - tracking never breaks the UX */
    }
  })();
}

// ── Public API ───────────────────────────────────────────────────

/**
 * Record one pageview and reset the engagement timer for this path.
 *
 * Handles session continuity internally: reads the stored session,
 * rolls it over if idle, and marks the row as first_in_session when
 * the session is fresh.
 *
 * @param path           Canonical same-origin path, already locale-stripped.
 * @param locale         Resolved locale ("az"/"ru"/"en") or null.
 * @param referrerPath   For SPA navigations: the previous same-origin path.
 *                       First cold-start mount should pass null so the
 *                       server picks up document.referrer instead.
 */
export function trackPageview(
  path: string,
  locale: string | null,
  referrerPath: string | null,
): void {
  if (typeof window === "undefined") return;
  const prevPath = active?.path ?? null;

  // SPA navigation: flush the engaged time banked on the OLD path
  // before we overwrite `active`, otherwise everything accrued since
  // its last 15 s heartbeat is silently dropped on every client-side
  // navigation (a systematic undercount for multi-page sessions).
  if (active && active.path !== path) {
    trackHeartbeat();
  }

  const now = Date.now();
  const stored = readSession();
  let session_id: string;
  let is_first_in_session = false;
  if (stored && now - stored.last_activity < SESSION_IDLE_MS) {
    session_id = stored.id;
  } else {
    session_id = uuidv4();
    is_first_in_session = true;
  }
  writeSession({ id: session_id, last_activity: now });

  const sp = new URLSearchParams(window.location.search);

  const body: Record<string, unknown> = {
    path,
    session_id,
    locale: locale || undefined,
    is_first_in_session,
  };
  if (is_first_in_session) {
    // Full referrer only on cold-start of a session. For SPA navs we
    // already know the previous path and the server would just have
    // to re-derive the same value.
    if (document.referrer) body.referrer_raw = document.referrer;
    // Campaign params ride with the first pageview of the session so
    // the row has full attribution context without a join.
    const utm_source = sp.get("utm_source");
    const utm_medium = sp.get("utm_medium");
    const utm_campaign = sp.get("utm_campaign");
    const utm_content = sp.get("utm_content");
    const utm_term = sp.get("utm_term");
    if (utm_source) body.utm_source = utm_source.slice(0, 80);
    if (utm_medium) body.utm_medium = utm_medium.slice(0, 80);
    if (utm_campaign) body.utm_campaign = utm_campaign.slice(0, 120);
    if (utm_content) body.utm_content = utm_content.slice(0, 120);
    if (utm_term) body.utm_term = utm_term.slice(0, 120);
    // Google Ads click IDs. Auto-tagging (default ON in Ads) appends
    // gclid (Search / Display / PMax) or gbraid / wbraid (iOS app
    // campaigns + privacy-restricted web) to the landing URL, but
    // does NOT set UTM params. Capture them so paid traffic is
    // attributed at the campaign level instead of just showing up as
    // "google.com" in the referrer host.
    const gclid = sp.get("gclid");
    const gbraid = sp.get("gbraid");
    const wbraid = sp.get("wbraid");
    if (gclid) body.gclid = gclid.slice(0, 200);
    if (gbraid) body.gbraid = gbraid.slice(0, 200);
    if (wbraid) body.wbraid = wbraid.slice(0, 200);
    // Non-Google click IDs. fbclid lands on every Meta-platform
    // click (Facebook + Instagram - including link-in-bio
    // openings from the IG app), ttclid is TikTok's equivalent,
    // msclkid is Microsoft Ads (Bing). Without these we'd see
    // an Instagram link-in-bio click as "campaign" via
    // utm_source=ig but lose the platform-level trail back to
    // Meta. The 200-char cap mirrors gclid - these tokens are
    // typically much shorter (~ 90 chars) but Meta occasionally
    // appends extra context that grows the value.
    const fbclid = sp.get("fbclid");
    const ttclid = sp.get("ttclid");
    const msclkid = sp.get("msclkid");
    if (fbclid) body.fbclid = fbclid.slice(0, 200);
    if (ttclid) body.ttclid = ttclid.slice(0, 200);
    if (msclkid) body.msclkid = msclkid.slice(0, 200);
    // Performance Max attribution. gad_source is the only reliable
    // "this was PMax" signal (Google's numeric surface ID; PMax = 5)
    // and gad_campaignid is the asset-group / sub-campaign that
    // actually served. Both ride the landing URL alongside gclid for
    // any Google Ads click; PMax fills these in even when keyword /
    // ad-group ValueTrack tokens come back empty.
    const gad_source_raw = sp.get("gad_source");
    const gad_source = gad_source_raw ? parseInt(gad_source_raw, 10) : NaN;
    if (Number.isFinite(gad_source) && gad_source >= 0 && gad_source <= 99) {
      body.gad_source = gad_source;
    }
    const gad_campaignid = sp.get("gad_campaignid");
    if (gad_campaignid) body.gad_campaignid = gad_campaignid.slice(0, 20);
  } else if (referrerPath) {
    body.referrer_path = referrerPath;
  }

  // Automated browsers (our Lighthouse and Playwright checks among
  // them) look like plain Chrome; the server stores them as bots.
  if (typeof navigator !== "undefined" && navigator.webdriver) body.automated = true;
  post("/track/pageview", body);

  // Reset the engagement state so the next heartbeat accounts for
  // time on the new path, not the old one.
  active = {
    session_id,
    path,
    locale: locale || null,
    duration_ms: 0,
    visible_since: now,
    is_active:
      typeof document !== "undefined" &&
      document.visibilityState === "visible",
  };
  // Events fired before this visit existed (a child's mount effect runs
  // before the provider's) were dropped; send them now.
  if (pendingEvents.length) {
    const queued = pendingEvents.splice(0);
    for (const [name, props] of queued) trackEvent(name, props);
  }
  // Where the session came in, for the hub's one suggestion
  // (lib/entry-context).
  if (is_first_in_session) captureEntry(path, window.location.search);
  // A link of ours (`?via=digest&rec=...`) opened inside a running session
  // (once per page: a re-run on the same path is no new landing).
  else if (prevPath !== path && /[?&]via=/.test(window.location.search)) noteVia(path, window.location.search);
}

/**
 * Flush engaged time on the current path to the server. Safe to call
 * at any moment - short delta, no-op, or error all self-heal on the
 * next tick. Called by the 15 s interval + visibility change + final
 * pagehide beacon.
 *
 * @param useBeacon  True on pagehide so we use navigator.sendBeacon
 *                   (survives unload more reliably than keepalive-fetch).
 */
export function trackHeartbeat(useBeacon = false): void {
  if (!active) return;
  const now = Date.now();
  if (active.is_active) {
    const delta = now - active.visible_since;
    if (delta > 0) active.duration_ms += delta;
    active.visible_since = now;
  }
  const duration_ms = Math.max(0, Math.min(active.duration_ms, MAX_HEARTBEAT_MS));
  post(
    "/track/heartbeat",
    {
      session_id: active.session_id,
      path: active.path,
      duration_ms,
    },
    useBeacon,
  );

  // Also refresh the stored session's last_activity stamp so the
  // idle-rollover timer doesn't expire in the middle of a reading
  // session where the user is still clearly present. BUT never
  // resurrect a session that already idle-expired: a tab left open
  // overnight fires a heartbeat on return, and unconditionally
  // re-stamping here made that day-old session read as fresh - the
  // 30-min rollover documented above never triggered for kept-open
  // tabs, so return visits lost their new-session attribution.
  // Only count VISIBLE presence toward session liveness - a hidden
  // tab's (throttled) heartbeats must not keep the session alive.
  const stored = readSession();
  const expired =
    stored != null &&
    stored.id === active.session_id &&
    now - stored.last_activity >= SESSION_IDLE_MS;
  if (active.is_active && !expired) {
    writeSession({ id: active.session_id, last_activity: now });
  }
}

/**
 * Toggle the engagement accumulator on/off. Called by the visibility-
 * change listener. When a tab goes hidden we flush so credited time
 * reflects "up to the moment they left" rather than "next heartbeat
 * tick whenever that would be".
 */
export function setVisibilityActive(is_active: boolean): void {
  if (!active) return;
  const now = Date.now();
  if (active.is_active && !is_active) {
    // Going from visible → hidden: bank the delta.
    active.duration_ms += now - active.visible_since;
    active.is_active = false;
    // Flush so the server has the up-to-date number; if the user
    // never comes back, this is the last honest reading we'll send.
    trackHeartbeat();
  } else if (!active.is_active && is_active) {
    // Going from hidden → visible: restart the clock, don't retro-
    // actively bill the hidden period.
    active.visible_since = now;
    active.is_active = true;
    // If the stored session idle-expired while the tab was hidden,
    // this is a RETURN VISIT in a kept-open tab: roll a fresh session
    // by re-firing a pageview for the current path (mints the new
    // session id + its first-in-session row, so heartbeats have a row
    // to land on). Without this, mobile users who background the tab
    // for days silently continue a stale session forever.
    const stored = readSession();
    if (
      stored != null &&
      stored.id === active.session_id &&
      now - stored.last_activity >= SESSION_IDLE_MS
    ) {
      trackPageview(active.path, active.locale, null);
    }
  }
}

/**
 * Final flush on pagehide. Uses sendBeacon so the request survives
 * the browser tearing the tab down.
 */
export function trackPageEnd(): void {
  if (!active) return;
  const now = Date.now();
  if (active.is_active) {
    active.duration_ms += now - active.visible_since;
    active.visible_since = now;
  }
  trackHeartbeat(true);
}

/**
 * Record one discrete interaction event.
 *
 * Call from anywhere in the client tree. No-ops on SSR; no-ops when
 * no active visit exists (shouldn't happen in practice because the
 * TrackingProvider always fires a pageview on mount, but guarding
 * keeps call sites safe). Fires fire-and-forget via fetch+keepalive.
 *
 * Naming convention: dot-separated, lowercase, stable
 * (``cta.hero.submit``, ``intent.chip.click``, ``pwa.promo.outcome``).
 * The server regex caps to ``[a-z0-9._-]`` so call-sites that drift
 * off-script silently drop instead of polluting the index.
 *
 * @param name   Event name.
 * @param props  Flexible payload (``{ chip: "cheapest" }`` etc).
 *               Must be JSON-serialisable; server truncates > 1 KB.
 */
export function trackEvent(
  name: string,
  props?: Record<string, unknown>,
  /** On pagehide: a keepalive fetch issued while the page unloads is
   *  often dropped (a listing page's `listing.close` never arrived), a
   *  beacon is not. The beacon cannot carry X-Client-Id; the server
   *  reads the same id from the menzil_cid cookie then. */
  useBeacon = false,
): void {
  if (typeof window === "undefined") return;
  if (!active) {
    // No active visit yet: a component's mount effect runs before the
    // TrackingProvider's, so on-mount events (an SEO page view) arrived
    // here first and were lost. Hold a few until the visit starts;
    // never send them with a stale or empty session_id.
    if (pendingEvents.length < 20) pendingEvents.push([name, props]);
    return;
  }
  const body: Record<string, unknown> = {
    event_name: name,
    session_id: active.session_id,
    path: active.path,
  };
  if (active.locale) body.locale = active.locale;
  if (props && Object.keys(props).length) body.props = props;
  post("/track/event", body, useBeacon);
  // The same moment on the session recording's timeline (no-op when the
  // recorder is off; loaded lazily so this module stays light).
  void import("@/lib/replay/recorder").then(({ replayEvent }) => replayEvent(`product:${name}`, props)).catch(() => {});
}

// ── Product events (the app's ~55 capture points) ───────────────────
// Saves, statuses, upsell taps, chat, quick filters... These used to go
// to PostHog with a first-party copy; PostHog is gone (owner,
// 2026-10-16), so they land in tracking_events only, shaped exactly as
// the copy was: a name of at most 60 characters and flat props (short
// strings, numbers, booleans; nulls and objects dropped).
export function captureEvent(name: string, props?: Record<string, unknown>, opts?: { beacon?: boolean }): void {
  try {
    const compact: Record<string, unknown> = {};
    if (props && typeof props === "object") {
      for (const [k, v] of Object.entries(props)) {
        if (v == null || k.startsWith("$")) continue;
        const t = typeof v;
        if (t === "string") compact[k] = (v as string).slice(0, 140);
        else if (t === "number" || t === "boolean") compact[k] = v;
      }
    }
    trackEvent(name.slice(0, 60), Object.keys(compact).length ? compact : undefined, !!opts?.beacon);
  } catch {
    /* analytics must never break the page */
  }
}
